import { FC, useEffect, useState, useMemo, useCallback } from "react";
import { Box, useTheme } from "@mui/material";
import { FormikHelpers } from "formik";
import { Table } from "@vilocnv/allsetra-core";
import MilageRegExpendableRowCard from "components/cards/ReportCards/MilageRegExpendableRowCard/MilageRegExpendableRowCard";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import ReportEmptyState from "components/common/reports/ReportEmptyState";

// Data
import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
  useReportCommonStates,
  useReportInactiveObjects
} from "hooks";
import { MILAGE_REGISTRATION_TABLE_COLUMNS } from "app/data/constants";
import {
  commonReportsDataFormatter,
  commonReportsFilterFormValidationSchema,
  convertToNormalTime,
  reportsMilageRegistrationFormInitialValues,
  getStandardizedReportDates
} from "app/data/helpers";
import {
  buildReportFilterPayload,
  getResetFilterFormValues
} from "app/data/helpers/ReportHelpers";
import { generateReportThunk } from "app/features/reports/reportsActions";
import {
  getAllKeysByAccountThunk,
  resetReportsFilterData,
  setReportsDateValues,
  setReportsFilterData
} from "app/features";
import { selectAccountKeysState } from "app/data/selectors";
import ReportsCommonFilterForm from "components/forms/Reports/ReportsCommonFilterForm";
import { useMovementRideContext } from "contexts/MovementRideContext";

const MilageRegistration: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { showMovementRides } = useMovementRideContext();

  const {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    handleObjectsSearch,
    handleOnChangeReportFilterForm,
    reportsFilterData,
    filterValues,
    setFilterValues,
    mergedObjectOptions,
    getHydratedFormInitialValues,
    setSelectedLocalIds,
    resetReportDates
  } = useReportCommonStates();

  const [openModal, setOpenModal] = useState(false);
  const { applyInactiveFilter, statusLookupsReady, showInactiveObjects } =
    useReportInactiveObjects();
  const tablePending = reportsLoading || !statusLookupsReady;

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const [apiPayload, setApiPayload] = useState(() =>
    reportsFilterData && Object.keys(reportsFilterData).length
      ? reportsFilterData
      : null
  );

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const { formattedData, isShowKeyCol } = useMemo(() => {
    let reportsData = [...report];
    reportsData = reportsData.map((reportItem) => ({
      ...reportItem,
      items: reportItem?.items?.filter(
        (item: { startedWithoutIginition: boolean }) =>
          showMovementRides ? true : !item.startedWithoutIginition
      )
    }));
    const formatted = commonReportsDataFormatter(
      reportsData,
      "name",
      dateFormat
    );
    return {
      ...formatted,
      formattedData: applyInactiveFilter(formatted?.formattedData)
    };
  }, [report, dateFormat, showMovementRides, applyInactiveFilter]);

  const tableColumns = useMemo(
    () => MILAGE_REGISTRATION_TABLE_COLUMNS(t, isShowKeyCol),
    [t, showMovementRides, isShowKeyCol]
  );

  const milageRegistrationFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    setFilterValues(values);

    const payload = buildReportFilterPayload({
      values: {
        ...values,
        rideModes: values?.rideModes?.map(Number) || []
      },
      dateValues,
      reportType: 2,
      extra: { startedWithoutIgnition: true }
    });

    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const handleDateChange = useCallback((value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  }, []);

  const handleDateRangeSubmit = () => {
    const payload = buildReportFilterPayload({
      values: {
        ...filterValues,
        rideModes: filterValues?.rideModes?.map(Number) || []
      },
      dateValues,
      reportType: 2,
      normalizeDates: false,
      startDateOverride: convertToNormalTime(dateValues.startDate),
      endDateOverride: convertToNormalTime(dateValues.endDate, true),
      extra: { startedWithoutIgnition: true }
    });

    setApiPayload(payload);
  };

  const handleLoadReport = () => {
    const payload = buildReportFilterPayload({
      values: {
        ...filterValues,
        rideModes: filterValues?.rideModes?.map(Number) || []
      },
      dateValues,
      reportType: 2,
      normalizeDates: false,
      startDateOverride: convertToNormalTime(dateValues.startDate),
      endDateOverride: convertToNormalTime(dateValues.endDate, true),
      extra: { startedWithoutIgnition: true }
    });

    setApiPayload(payload);
    dispatch(setReportsFilterData(payload));
    dispatch(generateReportThunk(payload));
  };

  useEffect(() => {
    handleDateRangeSubmit();
  }, []);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId]);

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.mileageRegistration")}
        disabled={reportsLoading ? true : false}
      />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={reportsLoading}
          disableExportButton={formattedData.length > 0 ? false : true}
          reportType={2}
          reportExportTypeValue={2}
          openModal={openModal}
          setOpenModal={setOpenModal}
          exportModalButton={true}
          hideExportButton={true}
          showRideModes={true}
          showToggleMovementRides={true}
          handleLoadReport={handleLoadReport}
          formattedData={formattedData}
        />
        <ReportsCommonFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={milageRegistrationFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={mergedObjectOptions}
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          theme={theme}
          isMileageReport={true}
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
          formInitialValues={getHydratedFormInitialValues(
            reportsMilageRegistrationFormInitialValues
          )}
          formValidations={commonReportsFilterFormValidationSchema}
          onResetFilters={() => {
            dispatch(resetReportsFilterData());
            resetReportDates();
            setSelectedLocalIds([]);
            setApiPayload(null);
            setFilterValues(
              getResetFilterFormValues(
                reportsMilageRegistrationFormInitialValues
              )
            );
          }}
        />
        <Table
          columns={tableColumns}
          data={formattedData}
          progressPending={tablePending}
          noDataComponent={!tablePending ? <ReportEmptyState /> : null}
          expandableRows
          expandOnRowClicked
          expandableRowsComponent={MilageRegExpendableRowCard}
          expandableRowsComponentProps={{ isShowKeyCol: isShowKeyCol }}
          pagination={false}
          nestedRowPadding={"16px"}
          translator={t}
        />
      </Box>
    </Box>
  );
};

export default MilageRegistration;
